<template>
  <v-card class="overflow-hidden live-tracking-sidebar px-2">
    <v-menu offset-y>
      <template v-slot:activator="{ on, attrs, value }">
        <span
          class="black--text d-flex align-center w-100 border-bottom live-tracking-sidebar-menu"
          v-bind="attrs"
          v-on="on"
        >
          <span class="font-14 font-weight-bold">{{
            trackingEngineerStatus?.text
          }}</span>
          <v-avatar
            class="mx-2"
            size="23"
            :color="trackingEngineerStatus?.color"
          >
            <span class="white--text font-weight-medium">{{
              trackingEngineerStatus?.count
            }}</span>
          </v-avatar>
          <v-icon v-if="value">mdi-chevron-up</v-icon>
          <v-icon v-else>mdi-chevron-down</v-icon>
        </span>
      </template>
      <v-list tile>
        <v-list-item
          v-for="(row, index) in trackingEngineerStatusList"
          :key="`engineer-status-list-${index}`"
          @click="updateEngineerStatus(row)"
          link
        >
          <v-list-item-title class="font-14">
            {{ row.text }}
          </v-list-item-title>
          <v-list-item-avatar class="my-0">
            <v-avatar size="23" :color="row.color">
              <span class="white--text font-weight-medium">{{
                row.count
              }}</span>
            </v-avatar>
          </v-list-item-avatar>
        </v-list-item>
      </v-list>
    </v-menu>
    <v-sheet height="calc(100vh - 111px)" class="overflow-y-auto">
      <v-text-field
        dense
        filled
        solo
        flat
        color="cyan"
        hide-details
        v-model="search"
        clearable
        placeholder="Search staff..."
        append-icon="mdi-magnify"
        @input="filterEngineer"
        class="my-3"
        :loading="trackingEngineerLoading"
      />
      <div>
        <div
          v-if="trackingEngineerLoading"
          class="d-flex justify-center align-center my-4"
        >
          <v-progress-circular indeterminate color="cyan" />
        </div>
        <div v-else>
          <v-list
            tile
            style="
              max-height: calc(100vh - 168px);
              overflow-y: scroll;
              padding: 0;
            "
          >
            <v-list-item
              v-for="(row, index) in trackingEngineers"
              :key="`live-tracking-engineers-${index}`"
              class="border-top"
              :class="{
                'active-card': row?.uuid === trackingActiveEngineer?.uuid,
              }"
              @click="setActiveEngineer(row)"
            >
              <v-list-item-avatar>
                <v-img :src="row?.profile_logo ?? $defaultProfileImage" />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  <div class="d-flex align-center">
                    <div class="ellipsis-text">
                      {{ row.display_name }}
                    </div>
                    <v-spacer></v-spacer>
                    <div>
                      <template v-if="!row.is_tracking_enabled">
                        <v-tooltip right content-class="custom-right-tooltip">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              color="red"
                              class="ml-1"
                            >
                              mdi-map-marker-remove
                            </v-icon>
                          </template>
                          Location tracking is disabled for this technician.
                        </v-tooltip>
                      </template>
                      <template v-else>
                        <v-tooltip top content-class="custom-top-tooltip">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              class="ml-1"
                              :color="row.is_online ? 'green' : 'red'"
                            >
                              mdi-access-point
                            </v-icon>
                          </template>

                          {{
                            row.last_online_at
                              ? "Last online at " +
                                formatDateTime(row.last_online_at)
                              : "Technician has not been online yet"
                          }}
                        </v-tooltip>
                        <v-tooltip top content-class="custom-top-tooltip">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              class="ml-1"
                              :color="row.is_location_enabled ? 'green' : 'red'"
                            >
                              {{
                                row.is_location_enabled
                                  ? "mdi-map-marker"
                                  : "mdi-map-marker-off"
                              }}
                            </v-icon>
                          </template>
                          {{
                            row.is_location_enabled
                              ? "Technician has granted GPS permission."
                              : "Technician has not granted GPS permission."
                          }}
                        </v-tooltip>
                      </template>
                    </div>
                    <div  class="ml-1">
                      <v-tooltip right content-class="custom-right-tooltip">
                        <template v-slot:activator="{ on, attrs }">
                          <v-avatar
                            v-bind="attrs"
                            v-on="on"
                            size="20"
                            class="white--text"
                            color="blue"
                            >{{ row?.total_visits }}</v-avatar
                          >
                        </template>
                        Total Assigned Visits
                      </v-tooltip>
                    </div>
                  </div>
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ row?.designation }}
                </v-list-item-subtitle>
                <v-list-item-title
                  class="text-capitalize font-weight-500 font-size-16 mt-1"
                >
                  <v-tooltip top content-class="custom-top-tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        v-bind="attrs"
                        v-on="on"
                        small
                        label
                        color="blue"
                        outlined
                        text-color=""
                        ><span  style="overflow: hidden;">{{ row?.entity_relation?.name }}</span>
                      </v-chip>
                    </template>
                    <span>{{ row?.entity_relation?.name }}</span>
                  </v-tooltip>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
      </div>
    </v-sheet>
  </v-card>
</template>
<script>
import { mapGetters } from "vuex";
import {
  GET_ENGINEERS,
  SET_ACTIVE_ENGINEER,
  SET_ACTIVE_VISIT,
  SET_ENGINEER_STATUS,
  GET_ENGINEER_STATUS_LIST,
  GET_VISITS,
  GET_VISIT_STATUS_LIST,
} from "@/core/services/store/tracking.module";
import CommonMixin from "@/core/plugins/common-mixin";

export default {
  name: "live-tracking-engineer-sidebar",
  mixins: [CommonMixin],
  data() {
    return {
      search: null,
      timeout: null,
      timeoutDuration: 500,
    };
  },
  methods: {
    setActiveEngineer(row) {
      this.$store.commit(SET_ACTIVE_ENGINEER, row);
      this.$store.commit(SET_ACTIVE_VISIT, {});
      this.$store.dispatch(GET_VISIT_STATUS_LIST);
      this.$store.dispatch(GET_VISITS, {
        status: row?.id ? "all" : "unassigned",
      });
      this.$emit("click:engineer", row);
    },
    filterEngineer() {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(this.getEngineers, this.timeoutDuration);
    },
    updateEngineerStatus(row) {
      this.$store.commit(SET_ENGINEER_STATUS, row);
      this.setActiveEngineer({});
      this.filterEngineer();
    },
    getEngineers() {
      this.$store
        .dispatch(GET_ENGINEERS, { search: this.search })
        .catch((error) => {
          this.logError(error);
        });
    },
    getEngineerStatus() {
      this.$store.dispatch(GET_ENGINEER_STATUS_LIST).catch((error) => {
        this.logError(error);
      });
    },
  },
  mounted() {
    this.filterEngineer();
    this.getEngineerStatus();
  },
  computed: {
    ...mapGetters([
      "trackingActiveEngineer",
      "trackingEngineers",
      "trackingEngineerStatus",
      "trackingEngineerLoading",
      "trackingEngineerStatusList",
    ]),
  },
};
</script>
